import React, { useCallback, useEffect, useRef, useState } from "react";
import "./map.css";
import { ApiURL } from "../../../setting/GlobleVariable";
import $ from "jquery";
import {
  buildSeaticsTicketPayload,
  formatSeaticsDateTime,
  resolveVenueName,
} from "./seaticsMapHelpers";

const Seatics = ({
  setSidebarData,
  GroupsEvents,
  event,
  SeaticsData,
  containerId = "mapContainer",
  hideTicketList = false,
  onMapReady,
  suppressInternalLoader = false,
}) => {
  const [isLoading, setisLoading] = useState(true);
  const [error, setError] = useState(false);
  const [mapUnavailable, setMapUnavailable] = useState(false);

  const setSidebarDataRef = useRef(setSidebarData);
  setSidebarDataRef.current = setSidebarData;

  const initKeyRef = useRef(null);
  const mapScriptRef = useRef(null);
  const mapReadyFiredRef = useRef(false);

  const finishMapLoad = useCallback(() => {
    setisLoading(false);
    if (!mapReadyFiredRef.current) {
      mapReadyFiredRef.current = true;
      onMapReady?.();
    }
  }, [onMapReady]);

  const eventName = (event?.name || "").trim();
  const venueName = resolveVenueName(event);
  const dateTime = formatSeaticsDateTime(event);

  const loadScript = (src) => {
    return new Promise((resolve, reject) => {
      const script = document.createElement("script");
      script.src = src;
      script.async = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error(`Error loading ${src}`));
      document.body.appendChild(script);
    });
  };

  const applySeaticsConfig = () => {
    window.Seatics.config.largeScreenFormat = true;
    window.Seatics.config.hoverEnabled = true;
    window.Seatics.config.levelColors = [
      "#d86f6f",
      "#e2a6a6",
      "#f4ad58",
      "#ffd797",
      "#bc91c1",
      "#e5cae2",
      "#66b6dd",
      "#a3ddf8",
      "#abcb59",
      "#d2e08b",
      "#bfa082",
      "#d9d7ba",
      "#589caf",
      "#bcd3e7",
      "#fff871",
      "#fff7a9",
      "#9bc9a2",
      "#c7ddc8",
    ];
    window.Seatics.config.zoneColors = window.Seatics.config.levelColors;
    window.Seatics.config.useSuperLevels = true;
    window.Seatics.config.sectionNoTicketsColor = "#e2e2e2";
    window.Seatics.config.selectionScheme = 0;
    window.Seatics.config.selectionColor = "#ffff00";
    window.Seatics.config.selectionPulseFromColor = null;
    window.Seatics.config.ticketSeparationOptions = {
      packages: window.Seatics.TicketGroupSeparationOptions.Integrate,
      parking: window.Seatics.TicketGroupSeparationOptions.Separate,
      passes: window.Seatics.TicketGroupSeparationOptions.Separate,
      hotels: window.Seatics.TicketGroupSeparationOptions.Separate,
      unmappedFlex: window.Seatics.TicketGroupSeparationOptions.Integrate,
      unmappedStandard: window.Seatics.TicketGroupSeparationOptions.Separate,
    };
    window.Seatics.config.defaultSort = window.Seatics.SortOptions.PriceAsc;
    window.Seatics.config.saveFilterOptions = true;
    window.Seatics.config.enableMyList = false;
    window.Seatics.config.smallScreenMapLayout =
      window.Seatics.SmallScreenMapOptions.HiddenWithPreview;
    window.Seatics.config.showZoomControls = true;
    window.Seatics.config.skipPrecheckoutMobile = false;
    window.Seatics.config.skipPrecheckoutDesktop = false;
    window.Seatics.config.mapContained = false;
    window.Seatics.config.legendExpanded = false;
    window.Seatics.config.showCents = false;
    // Legacy layout: ticket list enabled (map-only mode hides list via CSS)
    window.Seatics.config.ticketListOnRight = true;
  };

  const initializeSeatics = useCallback(async () => {
    const ticketCount = GroupsEvents?.length ?? 0;
    const initKey = `${containerId}|${eventName}|${venueName}|${dateTime}|${ticketCount}`;

    if (!eventName || ticketCount === 0) {
      finishMapLoad();
      return;
    }
    if (SeaticsData?.length !== 0) return;
    if (initKeyRef.current === initKey) return;

    initKeyRef.current = initKey;
    mapReadyFiredRef.current = false;
    setisLoading(true);
    setError(false);
    setMapUnavailable(false);
    setSidebarDataRef.current([]);

    try {
      if (!window.Seatics) {
        await loadScript("https://mapwidget3.seatics.com/Api/framework");
      }
      if (!window.$) {
        await loadScript("https://code.jquery.com/jquery-3.6.0.min.js");
      }

      if (!dateTime) {
        setError(true);
        finishMapLoad();
        return;
      }

      const response = await fetch(`${ApiURL}/seatics/script/`);
      const result = await response.json();
      if (!response.ok || !result?.token) {
        setError(true);
        finishMapLoad();
        return;
      }

      const container = $(`#${containerId}`);
      if (!container.length) {
        setError(true);
        finishMapLoad();
        return;
      }

      const ticketPayload = buildSeaticsTicketPayload(GroupsEvents);

      const myCreateListFunction = async (Data) => {
        const ticketsData = Data[0]?.tickets;
        if (ticketsData) setSidebarDataRef.current(ticketsData);
      };

      applySeaticsConfig();

      window.callback = async (data) => {
        if (!data || data[0] === null) {
          setMapUnavailable(true);
          setSidebarDataRef.current(ticketPayload);
          finishMapLoad();
          return;
        }
        const eventData = data[0];
        const mapData = data[1];
        try {
          await window.Seatics.MapComponent.create({
            container,
            mapWidth: 700,
            mapHeight: window?.innerWidth < 600 ? 400 : 600,
            mapData,
            imgSrc: eventData.mapImage,
            mapName: eventData.mapName,
            vfsUrl: "https://vfs.Seatics.com",
            tickets: [],
            presentationInterface: {
              updateTicketsList: myCreateListFunction,
            },
            enableSectionInfoPopups: true,
            onSectionClick: true,
            // Legacy used ticketList: true — required for many venue maps to render
            ticketList: true,
          });
          if (window.Seatics?.MapComponent?.addTicketData) {
            window.Seatics.MapComponent.addTicketData(ticketPayload);
          }
          finishMapLoad();
        } catch (mapErr) {
          console.error("Seatics MapComponent.create failed", mapErr);
          setError(true);
          finishMapLoad();
        }
      };

      if (mapScriptRef.current?.parentNode) {
        mapScriptRef.current.parentNode.removeChild(mapScriptRef.current);
      }

      const Consumer_Key = process.env.REACT_APP_CONSUMER_KEY;
      const websiteConfigID = process.env.REACT_APP_WEBCONFIG_ID;
      const script = document.createElement("script");
      const venueParam = encodeURIComponent(venueName || "Venue");
      const nameParam = encodeURIComponent(eventName);
      script.src = `https://www.tn-apis.com/maps/v3/EventAndVenueInfo?callback=callback&websiteConfigId=${websiteConfigID}&consumerKey=${Consumer_Key}&access_token=${result.token}&eventName=${nameParam}&venue=${venueParam}&dateTime=${dateTime}`;
      mapScriptRef.current = script;
      document.body.appendChild(script);

      window.setTimeout(() => {
        if (window.Seatics?.MapComponent?.addTicketData) {
          window.Seatics.MapComponent.addTicketData(ticketPayload);
        }
        finishMapLoad();
      }, 5000);
    } catch (err) {
      console.error("Seatics init failed", err);
      setError(true);
      finishMapLoad();
      initKeyRef.current = null;
    }
  }, [
    GroupsEvents,
    containerId,
    eventName,
    venueName,
    dateTime,
    SeaticsData,
    finishMapLoad,
  ]);

  useEffect(() => {
    initializeSeatics();
  }, [initializeSeatics]);

  useEffect(() => {
    initKeyRef.current = null;
    mapReadyFiredRef.current = false;
    return () => {
      initKeyRef.current = null;
      mapReadyFiredRef.current = false;
      if (mapScriptRef.current?.parentNode) {
        mapScriptRef.current.parentNode.removeChild(mapScriptRef.current);
        mapScriptRef.current = null;
      }
    };
  }, [containerId, eventName]);

  const showNoInventoryOverlay =
    event?.available_count === 0 && (GroupsEvents?.length ?? 0) === 0;

  return (
    <div
      className={`w-[100%] h-[100%] bg-white relative ${
        hideTicketList ? "seatics-embed-map-only" : ""
      }`}
    >
      <div
        id={containerId}
        className="w-[100%] h-[400px] sm:h-[600px] 2xl:h-[100%]"
      />
      {isLoading && !suppressInternalLoader && (
        <div className="w-[100%] h-[100%] absolute top-0 left-0 z-40 bg-white flex justify-center place-items-center">
          <div
            className="inline-block h-[60px] w-[60px] animate-spin rounded-full border-4 border-solid border-current border-r-transparent text-black align-[-0.125em] motion-reduce:animate-[spin_.5s_linear_infinite]"
            role="status"
          >
            <span className="!absolute !-m-px !h-px !w-px !overflow-hidden !whitespace-nowrap !border-0 !p-0 ![clip:rect(0,0,0,0)]">
              Loading...
            </span>
          </div>
        </div>
      )}

      {showNoInventoryOverlay && (
        <div className="absolute top-0 left-0 w-[100%] h-[100%] bg-white z-50 flex justify-center place-items-center">
          <h2 className="text-black">No data found</h2>
        </div>
      )}
      {mapUnavailable && (
        <div className="absolute top-0 left-0 w-[100%] h-[100%] bg-white z-50 flex flex-col justify-center place-items-center p-6 text-center">
          <h2 className="text-black font-semibold mb-2">Seat map not available</h2>
          <p className="text-black/80 text-sm max-w-md">
            A seat map is not available for this venue. You can still browse and purchase tickets from the list below.
          </p>
        </div>
      )}
      {error && (
        <div className="absolute top-0 left-0 w-[100%] h-[100%] bg-white z-50 flex justify-center place-items-center">
          <h2 className="text-black">Something went wrong. No data found</h2>
        </div>
      )}
    </div>
  );
};

export default React.memo(Seatics);
